
// Three-cell date-of-birth input with smart focus management:
//   - auto-advances MM -> DD -> YYYY once a cell is "full" AND in range
//   - a full-but-out-of-range value (e.g. month 13, day 32, year 1899) does
//     NOT advance and gets a red "invalid" border until corrected
//   - backspace on an empty cell jumps focus back to the previous cell
// Bind with .sync, e.g. :mm.sync='client.dobM' :dd.sync='client.dobD' :yyyy.sync='client.dobY'
const FIELDS = ['mm', 'dd', 'yyyy']
const MAXLEN = { mm: 2, dd: 2, yyyy: 4 }

export default {
    props: {
        mm: { type: [String, Number], default: '' },
        dd: { type: [String, Number], default: '' },
        yyyy: { type: [String, Number], default: '' },
        // Whole-field invalid flag from the parent (applies red to all cells).
        invalid: { type: Boolean, default: false },
        // For reuse outside the scheduler (TypedDateInput.vue): a <label for>
        // target on the month cell, Bootstrap's form-control on /settings, and
        // non-birthday dates (Part A/B) that must not offer birthday autofill.
        id: { type: String, default: null },
        inputClass: { type: String, default: '' },
        label: { type: String, default: 'Birth' },
        autocomplete: { type: Boolean, default: true },
    },
    data () {
        return {
            // Per-cell range-validation state (red border, blocks advance).
            cellInvalid: { mm: false, dd: false, yyyy: false },
        }
    },
    computed: {
        // Single source of truth for both the template min/max attrs and the
        // range check. Year tops out at next year so upcoming birthdays work.
        bounds () {
            return {
                mm: { min: 1, max: 12 },
                dd: { min: 1, max: 31 },
                yyyy: { min: 1900, max: new Date().getFullYear() + 1 },
            }
        },
    },
    methods: {
        digitsOnly (v, max) {
            return String(v || '').replace(/\D/g, '').slice(0, max)
        },
        // Only a full-length cell is range-checked; partial input is "valid so far".
        isInRange (field, val) {
            const s = String(val || '')
            if (s.length < MAXLEN[field]) return true
            const n = parseInt(s, 10)
            const b = this.bounds[field]
            return Number.isFinite(n) && n >= b.min && n <= b.max
        },
        onInput (field, e) {
            const max = MAXLEN[field]
            const clean = this.digitsOnly(e.target.value, max)
            // Keep the visible input in sync even when we strip characters
            if (e.target.value !== clean) e.target.value = clean
            this.$emit(`update:${field}`, clean)

            const full = clean.length >= max
            const inRange = this.isInRange(field, clean)
            this.cellInvalid[field] = full && !inRange

            // Auto-advance only when this cell is full AND within range.
            if (full && inRange) {
                const idx = FIELDS.indexOf(field)
                const next = FIELDS[idx + 1]
                if (next && this.$refs[next]) {
                    this.$refs[next].focus()
                    this.$refs[next].select && this.$refs[next].select()
                }
            }
        },
        onKeydown (field, e) {
            // Backspace on an empty cell hops back to the previous cell so the
            // user can keep deleting without re-clicking.
            if (e.key === 'Backspace' && !e.target.value) {
                const idx = FIELDS.indexOf(field)
                const prev = FIELDS[idx - 1]
                if (prev && this.$refs[prev]) {
                    e.preventDefault()
                    const el = this.$refs[prev]
                    el.focus()
                    // place caret at the end
                    const len = el.value.length
                    el.setSelectionRange && el.setSelectionRange(len, len)
                }
            }
        },
        focus () {
            if (this.$refs.mm) this.$refs.mm.focus()
        },
    },
}
